import {
  fromIso8601DateTime,
  toLocaleTextAsDateTime,
} from "@/shared/lib/datetime";
import type { Loadable } from "@/shared/lib/loadable";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseIcon } from "@/shared/ui/BaseIcons";
import { BaseTable } from "@/shared/ui/base-table";
import type { Column } from "@/shared/ui/base-table/types";
import { OfferCargoStatusBadge } from "@/widgets/offer-cargo-status-badge";
import { CircularProgress, Stack, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { useNavigate } from "@tanstack/react-router";
import type { FreightMatching } from "@x-mile/api-contract";
import type React from "react";
import { useMemo } from "react";
import {
  type CONTRACT_STATUS,
  calculateContractOfferCargoStatus,
} from "./badge-status";

interface ContractTableProps {
  contractOfferCargos: Loadable<FreightMatching.schemas.OfferCargo[]>;
  deliveryProposalId?: number;
}

type ContractTableDataType = {
  id: number;
  companyName: string;
  contractStatus: CONTRACT_STATUS;
  createdAt: string;
};

export const ContractTable: React.FC<ContractTableProps> = ({
  contractOfferCargos,
  deliveryProposalId,
}) => {
  const navigate = useNavigate();
  const ContractTableComponent = BaseTable<ContractTableDataType>;

  const columns: Column<ContractTableDataType>[] = [
    {
      key: "companyName",
      label: "企業名",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.companyName}</Typography>;
      },
    },
    {
      key: "contractStatus",
      label: "契約状況",
      cell: (row) => {
        return (
          <OfferCargoStatusBadge
            statusType="delivery_proposal_contract"
            status={row.contractStatus}
          />
        );
      },
    },
    {
      key: "createdAt",
      label: "作成日時",
      cell: (row) => {
        return <Typography color={grey[900]}>{row.createdAt}</Typography>;
      },
    },
  ];

  const rows = useMemo(() => {
    if (contractOfferCargos.status !== "success" || !contractOfferCargos.data) {
      return [];
    }

    return contractOfferCargos.data.map((contract): ContractTableDataType => {
      const status = calculateContractOfferCargoStatus(contract);
      return {
        id: contract.id,
        companyName: contract.company?.name || "不明な企業",
        contractStatus: status,
        createdAt: toLocaleTextAsDateTime(
          fromIso8601DateTime(contract.createdAt),
        ),
      };
    });
  }, [contractOfferCargos]);

  const handleCreateClick = () => {
    if (deliveryProposalId) {
      navigate({
        to: "/admin/delivery-proposals/$id/contracts/create",
        params: { id: deliveryProposalId.toString() },
      });
    }
  };

  const createButton = deliveryProposalId ? (
    <BaseButton
      variant="contained"
      size="small"
      startIcon={<BaseIcon name="plus" htmlColor="white" />}
      onClick={handleCreateClick}
      sx={{
        fontSize: "14px",
      }}
    >
      契約を新規作成
    </BaseButton>
  ) : null;

  if (contractOfferCargos.status === "loading") {
    return (
      <Stack spacing={3} id="contract-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2" fontSize="18px" fontWeight={700}>
            契約一覧
          </Typography>
          {createButton}
        </Stack>
        <CircularProgress />
      </Stack>
    );
  }

  if (contractOfferCargos.status === "error") {
    return (
      <Stack spacing={3} id="contract-list">
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2" fontSize="18px" fontWeight={700}>
            契約一覧
          </Typography>
          {createButton}
        </Stack>
        <Typography color="error">
          {contractOfferCargos.error?.message ||
            "契約データの取得に失敗しました"}
        </Typography>
      </Stack>
    );
  }

  const contracts = contractOfferCargos.data || [];

  return (
    <Stack spacing={3} id="contract-list">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h2" fontSize="18px" fontWeight={700}>
          {`契約一覧${contracts.length > 0 ? ` (${contracts.length}件)` : ""}`}
        </Typography>
        {createButton}
      </Stack>
      <ContractTableComponent
        columns={columns}
        rows={rows}
        to="/admin/contracts/$id"
        params={(row) => ({
          id: row.id,
        })}
        noDataMessage="現在、契約がございません。"
      />
    </Stack>
  );
};
